export type FieldType = 'text' | 'number' | 'boolean' | 'list' | 'link' | 'links' | 'date' | 'gold' | 'statblock' | 'image';

/** Beschreibung einer Eigenschaft (Frontmatter-Feld) eines Notiz-Typs. */
export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  /** Bei `link`/`links`: auswählbare Notiz-Typen (leer = alle). */
  targetTypes?: string[];
  /** Vorschläge für Textfelder (frei überschreibbar). */
  options?: string[];
}

export type Visibility = 'dm' | 'all' | 'players';

export interface NoteTypeDef {
  key: string;
  label: string;
  color: string;
  fields: FieldDef[];
  templateMd: string;
  defaultVisibility: Visibility;
}

/** Version der Standardtypen; bestehende Kampagnen werden beim Start einmalig angehoben. */
export const NOTE_TYPES_VERSION = 6;

/** Frontmatter-Schlüssel für die Hierarchie (z. B. Ort liegt in Region). */
export const PARENT_KEY = 'parent';

const STATBLOCK_FIELD: FieldDef = { key: 'statblock', label: 'Statblock', type: 'statblock' };
/** Bild für Tokens auf Karten (`token: "[[Tokens/Lena.png]]"`). */
const TOKEN_FIELD: FieldDef = { key: 'token', label: 'Token-Bild', type: 'image' };
const CREATURES = ['nsc', 'monster', 'charakter'];

export const FALLBACK_TYPE_KEY = 'notiz';

/** Standard-Notiz-Typen, die jede neue Kampagne erhält. */
export const DEFAULT_NOTE_TYPES: readonly NoteTypeDef[] = [
  {
    key: 'startseite',
    label: 'Startseite',
    color: '#e8b04a',
    fields: [],
    templateMd: '# Übersicht\n\n',
    defaultVisibility: 'dm',
  },
  {
    key: 'raum',
    label: 'Raum',
    color: '#5b8def',
    fields: [
      { key: 'room_number', label: 'Raumnummer', type: 'text' },
      { key: 'room_variant', label: 'Zusatz', type: 'text' },
    ],
    templateMd: '## Beschreibung\n\n## Begegnungen\n\n## Schätze\n\n:::dm\nGeheimnisse des Raums\n:::\n',
    defaultVisibility: 'dm',
  },
  {
    key: 'monster',
    label: 'Monster',
    color: '#d9534f',
    fields: [{ key: 'habitat', label: 'Lebensraum', type: 'link', targetTypes: ['ort', 'raum'] }, TOKEN_FIELD, STATBLOCK_FIELD],
    templateMd: '## Taktik\n\n## Beute\n\n',
    defaultVisibility: 'dm',
  },
  {
    key: 'nsc',
    label: 'NSC',
    color: '#4caf7a',
    fields: [
      { key: 'role', label: 'Rolle', type: 'text' },
      { key: 'location', label: 'Ort', type: 'link', targetTypes: ['ort', 'raum'] },
      { key: 'faction', label: 'Fraktion', type: 'link', targetTypes: ['fraktion'] },
      TOKEN_FIELD,
      STATBLOCK_FIELD,
    ],
    templateMd: '## Erscheinung\n\n## Persönlichkeit\n\n## Wissen\n\n',
    defaultVisibility: 'dm',
  },
  {
    key: 'gegenstand',
    label: 'Gegenstand',
    color: '#b57edc',
    fields: [
      {
        key: 'rarity',
        label: 'Seltenheit',
        type: 'text',
        options: ['gewöhnlich', 'ungewöhnlich', 'selten', 'sehr selten', 'legendär', 'Artefakt'],
      },
      { key: 'attunement', label: 'Einstimmung', type: 'boolean' },
      { key: 'value_gp', label: 'Wert', type: 'gold' },
      { key: 'owner', label: 'Besitzer', type: 'link', targetTypes: CREATURES },
    ],
    templateMd: '## Beschreibung\n\n## Eigenschaften\n\n',
    defaultVisibility: 'dm',
  },
  {
    key: 'handout',
    label: 'Handout',
    color: '#c9a66b',
    fields: [{ key: 'found_in', label: 'Fundort', type: 'link', targetTypes: ['raum', 'ort'] }],
    templateMd: '',
    defaultVisibility: 'dm',
  },
  {
    key: 'hintergrund',
    label: 'Hintergrund',
    color: '#8d6e63',
    fields: [],
    templateMd: '',
    defaultVisibility: 'dm',
  },
  {
    key: 'ort',
    label: 'Ort',
    color: '#26a69a',
    fields: [
      {
        key: 'place_kind',
        label: 'Art',
        type: 'text',
        options: ['Kontinent', 'Region', 'Reich', 'Stadt', 'Dorf', 'Stadtviertel', 'Gebäude', 'Location', 'Dungeon', 'Wildnis'],
      },
      { key: PARENT_KEY, label: 'Liegt in', type: 'link', targetTypes: ['ort'] },
    ],
    templateMd: '## Beschreibung\n\n',
    defaultVisibility: 'dm',
  },
  {
    key: 'fraktion',
    label: 'Fraktion',
    color: '#ef8a3c',
    fields: [
      { key: 'leader', label: 'Anführer', type: 'link', targetTypes: CREATURES },
      { key: 'headquarters', label: 'Sitz', type: 'link', targetTypes: ['ort', 'raum'] },
      { key: 'members', label: 'Mitglieder', type: 'links', targetTypes: CREATURES },
    ],
    templateMd: '## Ziele\n\n## Mitglieder\n\n',
    defaultVisibility: 'dm',
  },
  {
    key: 'quest',
    label: 'Quest',
    color: '#f06292',
    fields: [
      { key: 'status', label: 'Status', type: 'text', options: ['offen', 'aktiv', 'erledigt', 'gescheitert'] },
      { key: 'giver', label: 'Auftraggeber', type: 'link', targetTypes: CREATURES },
      { key: 'location', label: 'Ort', type: 'link', targetTypes: ['ort', 'raum'] },
      { key: 'reward_gp', label: 'Belohnung', type: 'gold' },
    ],
    templateMd: '## Auftrag\n\n## Belohnung\n\n',
    defaultVisibility: 'dm',
  },
  {
    key: 'charakter',
    label: 'Charakter',
    color: '#42a5f5',
    fields: [
      { key: 'player', label: 'Spieler', type: 'text' },
      { key: 'class', label: 'Klasse', type: 'text' },
      { key: 'level', label: 'Stufe', type: 'number' },
      { key: 'species', label: 'Spezies / Volk', type: 'text' },
      { key: 'background', label: 'Hintergrund', type: 'text' },
      TOKEN_FIELD,
      STATBLOCK_FIELD,
    ],
    templateMd: '',
    defaultVisibility: 'dm',
  },
  {
    key: 'session',
    label: 'Session',
    color: '#78909c',
    fields: [
      { key: 'number', label: 'Nummer', type: 'number' },
      { key: 'date', label: 'Datum', type: 'date' },
      { key: 'time', label: 'Beginn', type: 'text' },
      { key: 'end_time', label: 'Ende', type: 'text' },
      { key: 'location', label: 'Ort', type: 'text' },
      { key: 'characters', label: 'Anwesend', type: 'links', targetTypes: ['charakter'] },
    ],
    templateMd: '## Zusammenfassung\n\n## Log\n\n',
    defaultVisibility: 'dm',
  },
  {
    key: 'karte',
    label: 'Karte',
    color: '#c97b3d',
    fields: [
      { key: 'image', label: 'Kartenbild', type: 'image' },
      { key: 'map_kind', label: 'Art', type: 'text', options: ['Weltkarte', 'Regionalkarte', 'Stadtkarte', 'Dungeon', 'Kampfkarte'] },
      { key: 'place', label: 'Zeigt', type: 'link', targetTypes: ['ort', 'raum'] },
    ],
    templateMd: '## Beschreibung\n\n',
    defaultVisibility: 'dm',
  },
  {
    key: 'notiz',
    label: 'Notiz',
    color: '#9e9e9e',
    fields: [],
    templateMd: '',
    defaultVisibility: 'dm',
  },
];

/** Felder, die mit Version 2 durch neue ersetzt wurden (Statblock, „Liegt in“). */
const OBSOLETE_FIELDS: Record<string, string[]> = {
  monster: ['cr', 'ac', 'hp', 'statblock_format'],
  ort: ['region'],
  charakter: ['sheet_format'],
};

/**
 * Hebt die Feldliste eines bestehenden Typs auf die aktuellen Standardfelder an:
 * Standardfelder in Standard-Reihenfolge (eigene Bezeichnungen bleiben erhalten),
 * danach eigene Felder des DMs. Ersetzte Felder bleiben, wenn Notizen sie nutzen.
 */
export function upgradeTypeFields(typeKey: string, current: FieldDef[], usedKeys: ReadonlySet<string> = new Set()): FieldDef[] {
  const def = DEFAULT_NOTE_TYPES.find((t) => t.key === typeKey);
  if (!def) return current;
  const obsolete = new Set((OBSOLETE_FIELDS[typeKey] ?? []).filter((k) => !usedKeys.has(k)));
  const byKey = new Map(current.map((f) => [f.key, f]));
  const merged = def.fields.map((f) => {
    const old = byKey.get(f.key);
    return old ? { ...f, label: old.label || f.label } : f;
  });
  const extra = current.filter((f) => !def.fields.some((d) => d.key === f.key) && !obsolete.has(f.key));
  return [...merged, ...extra];
}

export function typeHasStatblock(fields: readonly { type: string }[] | undefined): boolean {
  return !!fields?.some((f) => f.type === 'statblock');
}
